import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { capitalUses, investmentPathway, raiseClosing, upsideCallout, yearThreeProof } from '../../data/deckSlide16';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4;
const USE_X = [560, 1080];
const USE_Y = [326, 486];
const USE_W = 448;
export function RaiseSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(700, 1); // the raise
    at(1700, 2); // use of capital
    at(3400, 3); // pathway + proof
    at(4800, 4); // upside + closing
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2618" data-mp="183wwsvc9onu0">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2619" data-mp="184f2eiktkeel">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 70% 60% at 22% -18%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2620" data-mp="13411rlljs6ql" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2621" data-mp="1qsjz56326z3w">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2622" data-mp="1h1pe251ju5k1" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2623" data-mp="1c4p59mdbeop8">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2624" data-mp="24twcd9qu4mm3">
          17 · The Raise
        </motion.span>

        {/* Headline */}
        <motion.h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 126,
        width: 1050
      }} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.15,
        ease
      }} data-id="element-2625" data-mp="3pbf2nza4at17">
          $2.5M to scale what is already built.
        </motion.h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 194,
        width: 900
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.6,
        ease
      }} data-id="element-2626" data-mp="36exbpk33ex60">
          Capital to launch VEXU in the U.S., scale distribution and accelerate
          VEXU Business — while continuing to strengthen the underlying AI
          engine.
        </motion.p>

        {/* The anchor */}
        <motion.div className="absolute" style={{
        left: 112,
        top: 320,
        width: 400
      }} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 12
      }} transition={{
        duration: 1,
        ease
      }} data-id="element-2627" data-mp="2p1dztgbxkjsi">
          <p className="text-[80px] font-semibold leading-none tracking-[-0.04em] text-[#0B1712] tabular-nums" data-id="element-2628" data-mp="3crhzxdpwu1er">
            $2.5M
          </p>
          <p className="mt-4 text-[15px] font-semibold uppercase tracking-[0.26em] text-vexu-700" data-id="element-2629" data-mp="3mr8tnd6nccds">
            SAFE
          </p>
        </motion.div>

        {/* Upside */}
        <motion.div className="absolute rounded-2xl border border-vexu/25 bg-vexu/[0.05] px-6 py-5" style={{
        left: 112,
        top: 512,
        width: 400
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(4) ? 1 : 0,
        y: shown(4) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2630" data-mp="3hq31bggwer4s">
          <p className="text-[13px] font-semibold leading-snug text-vexu-700" data-id="element-2631" data-mp="jg16s1gysglt">
            {upsideCallout.title}
          </p>
          <p className="mt-2 text-[12px] leading-snug text-[#7C8B82]" data-id="element-2632" data-mp="ogfefienlojb">
            {upsideCallout.detail}
          </p>
        </motion.div>

        {/* Use of capital */}
        {capitalUses.map((use, i) => <motion.div key={use.index} className="absolute border-t border-[#0B1712]/[0.09] pt-4" style={{
        left: USE_X[i % 2],
        top: USE_Y[Math.floor(i / 2)],
        width: USE_W
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(2) ? 1 : 0,
        y: shown(2) ? 0 : 10
      }} transition={{
        duration: 0.8,
        delay: i * 0.14,
        ease
      }} data-id="element-2633" data-mp="3rxnt5te3489t" data-mpi={i}>
            <div className="flex items-baseline gap-3" data-id="element-2634" data-mp="3d6om4mswdrsc">
              <span className="text-[10.5px] font-semibold tracking-[0.22em] text-vexu-700/60 tabular-nums" data-id="element-2635" data-mp="3d53ryyahwav0">
                {use.index}
              </span>
              <span className="text-[15px] font-semibold uppercase tracking-[0.12em] text-[#0B1712]" data-id="element-2636" data-mp="30pnms53b4h1q">
                {use.area}
              </span>
            </div>
            <p className="mt-2.5 text-[12px] text-[#8A998F]" data-id="element-2637" data-mp="2rnwf4up7i058">{use.tags}</p>
            <p className="mt-2 text-[13.5px] leading-snug text-[#33463B]" data-id="element-2638" data-mp="2rq0i9afa6v4g">
              {use.outcome}
            </p>
          </motion.div>)}

        {/* Year-3 proof points */}
        <motion.div className="absolute" style={{
        left: USE_X[0],
        top: 644
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(3) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.25,
        ease
      }} data-id="element-2639" data-mp="2hws2i1n06rdk">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-[#A3AFA7]" data-id="element-2640" data-mp="23yv7vas40v1j">
            Year-3 Base Case
          </p>
          <div className="mt-3.5 flex gap-12" data-id="element-2641" data-mp="1yzqu06u8q94v">
          {yearThreeProof.map(item => <div key={item.label} data-id="element-2642" data-mp="1pa1t5uuidwfk">
              <p className="text-[19px] font-semibold tracking-[-0.015em] text-[#33463B] tabular-nums" data-id="element-2643" data-mp="1pdzemn4k702m">
                {item.value}
              </p>
              <p className="mt-1 text-[11.5px] text-[#A3AFA7]" data-id="element-2644" data-mp="1fei8y5pb4m6n">{item.label}</p>
            </div>)}
          </div>
        </motion.div>

        {/* Investment pathway */}
        <motion.div className="absolute flex items-center gap-4 border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 104
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(3) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2645" data-mp="281um5p9kvhna">
          {investmentPathway.map((step, i) => <React.Fragment key={step} data-id="element-2646" data-mp="2391jr81l0led" data-mpi={i}>
              {i > 0 && <span className="text-[12px] text-[#C3CCC5]" data-id="element-2647" data-mp="2qzxfuhd2m2rf">→</span>}
              <span className={`text-[15px] ${i === 0 ? 'font-semibold text-[#0B1712]' : i === investmentPathway.length - 1 ? 'font-semibold text-vexu-700' : 'text-[#33463B]'}`} data-id="element-2648" data-mp="2vrf90j8uqz1u">
                {step}
              </span>
            </React.Fragment>)}
        </motion.div>

        {/* Closing */}
        <motion.p className="absolute text-[19px] font-semibold tracking-[-0.015em] text-[#0B1712]" style={{
        left: 112,
        bottom: 48
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.35,
        ease
      }} data-id="element-2649" data-mp="p482xv35l417">
          {raiseClosing}
        </motion.p>
      </div>
    </SlideStage>;
}